import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Gauge,
  Video,
  Link as LinkIcon,
  Users,
  Settings,
  Menu,
  X,
  Plus,
  Trash2,
  Edit,
  Save,
  Eye,
  FileText,
  Home,
  Info,
  CreditCard,
  HelpCircle,
  PlayCircle,
  MessageSquare,
  Upload,
  Loader2,
  Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Component
const Logo = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_552d4860-4146-493e-8e68-213c46e43a66/artifacts/eqpwekmt_Nettrix%20Logo.png"
    alt="Nettrix Invest"
    className="h-8 w-auto"
  />
);

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  
  const menuItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/content', label: 'Edit Content', icon: FileText },
    { path: '/admin/urgency-meter', label: 'Urgency Meter', icon: Gauge },
    { path: '/admin/calculator', label: 'Calculator', icon: Calculator },
    { path: '/admin/testimonials', label: 'Testimonials', icon: Video },
    { path: '/admin/social-links', label: 'Social Links', icon: LinkIcon },
    { path: '/admin/leads', label: 'Leads', icon: Users },
    { path: '/admin/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#0A0A0A] border-r border-white/10 z-50 transform transition-transform lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <Logo />
            <button className="lg:hidden text-white" onClick={() => setIsOpen(false)}>
              <X size={24} />
            </button>
          </div>
          
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30' 
                      : 'text-[#A3A3A3] hover:bg-white/5 hover:text-white'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>
        
        <div className="absolute bottom-6 left-6 right-6">
          <Link
            to="/"
            className="flex items-center justify-center gap-2 px-4 py-3 bg-[#141414] border border-white/10 rounded-lg text-[#A3A3A3] hover:text-white hover:border-[#D4AF37]/30 transition-colors"
            data-testid="view-site"
          >
            <Eye size={18} />
            <span>View Site</span>
          </Link>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const [stats, setStats] = useState({ totalLeads: 0, newLeads: 0, testimonials: 0, socialLinks: 0 });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [leadsRes, testimonialsRes, linksRes] = await Promise.all([
          axios.get(`${API}/admin/leads`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/testimonials`).catch(() => ({ data: [] })),
          axios.get(`${API}/admin/social-links`).catch(() => ({ data: [] }))
        ]);
        
        setStats({
          totalLeads: leadsRes.data?.length || 0,
          newLeads: leadsRes.data?.filter(l => l.status === 'new').length || 0,
          testimonials: testimonialsRes.data?.length || 0,
          socialLinks: linksRes.data?.length || 0
        });
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };
    fetchStats();
  }, []);

  return (
    <div data-testid="dashboard-overview">
      <h1 className="text-3xl font-heading font-medium text-white mb-8">Dashboard</h1>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[#A3A3A3] text-sm">Total Leads</p>
              <p className="text-2xl font-bold text-white">{stats.totalLeads}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-[#22C55E]" />
            </div>
            <div>
              <p className="text-[#A3A3A3] text-sm">New Leads</p>
              <p className="text-2xl font-bold text-white">{stats.newLeads}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <Video className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[#A3A3A3] text-sm">Testimonials</p>
              <p className="text-2xl font-bold text-white">{stats.testimonials}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
              <LinkIcon className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[#A3A3A3] text-sm">Social Links</p>
              <p className="text-2xl font-bold text-white">{stats.socialLinks}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quick Links */}
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6">
        <h2 className="text-xl font-medium text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link to="/admin/content" className="p-4 bg-[#141414] border border-white/10 rounded-lg hover:border-[#D4AF37]/30 transition-colors text-center">
            <FileText className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-white text-sm">Edit Content</p>
          </Link>
          <Link to="/admin/urgency-meter" className="p-4 bg-[#141414] border border-white/10 rounded-lg hover:border-[#D4AF37]/30 transition-colors text-center">
            <Gauge className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-white text-sm">Urgency Meter</p>
          </Link>
          <Link to="/admin/testimonials" className="p-4 bg-[#141414] border border-white/10 rounded-lg hover:border-[#D4AF37]/30 transition-colors text-center">
            <Video className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-white text-sm">Testimonials</p>
          </Link>
          <Link to="/admin/leads" className="p-4 bg-[#141414] border border-white/10 rounded-lg hover:border-[#D4AF37]/30 transition-colors text-center">
            <Users className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
            <p className="text-white text-sm">View Leads</p>
          </Link>
        </div>
      </div>
    </div>
  );
};

// Content Editor Page
const ContentEditorPage = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content`);
      setContent(res.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const saveSection = async (section, data) => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/${section}`, data);
      toast.success(`${section.replace('_', ' ')} saved successfully!`);
      fetchContent();
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white">Loading content...</div>;
  }

  return (
    <div data-testid="content-editor">
      <h1 className="text-3xl font-heading font-medium text-white mb-8">Edit Website Content</h1>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 lg:grid-cols-7 gap-2 bg-[#0A0A0A] p-2 rounded-lg mb-6">
          <TabsTrigger value="hero" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Hero</TabsTrigger>
          <TabsTrigger value="about" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">About</TabsTrigger>
          <TabsTrigger value="how_it_works" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">How It Works</TabsTrigger>
          <TabsTrigger value="plans" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Plans</TabsTrigger>
          <TabsTrigger value="why_choose_us" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Why Choose</TabsTrigger>
          <TabsTrigger value="how_to_start" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">How to Start</TabsTrigger>
          <TabsTrigger value="footer" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Footer</TabsTrigger>
        </TabsList>
        
        {/* Hero Content */}
        <TabsContent value="hero">
          <HeroEditor content={content?.hero} onSave={(data) => saveSection('hero', data)} saving={saving} />
        </TabsContent>
        
        {/* About Content */}
        <TabsContent value="about">
          <AboutEditor content={content?.about} onSave={(data) => saveSection('about', data)} saving={saving} />
        </TabsContent>
        
        {/* How It Works */}
        <TabsContent value="how_it_works">
          <HowItWorksEditor content={content?.how_it_works} onSave={(data) => saveSection('how_it_works', data)} saving={saving} />
        </TabsContent>
        
        {/* Plans */}
        <TabsContent value="plans">
          <PlansEditor content={content?.plans} onSave={(data) => saveSection('plans', data)} saving={saving} />
        </TabsContent>
        
        {/* Why Choose Us */}
        <TabsContent value="why_choose_us">
          <WhyChooseEditor content={content?.why_choose_us} onSave={(data) => saveSection('why_choose_us', data)} saving={saving} />
        </TabsContent>
        
        {/* How to Start */}
        <TabsContent value="how_to_start">
          <HowToStartEditor content={content?.how_to_start} onSave={(data) => saveSection('how_to_start', data)} saving={saving} />
        </TabsContent>
        
        {/* Footer */}
        <TabsContent value="footer">
          <FooterEditor content={content?.footer} onSave={(data) => saveSection('footer', data)} saving={saving} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Hero Editor Component
const HeroEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || {});

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white flex items-center gap-2">
        <Home className="w-5 h-5 text-[#D4AF37]" /> Hero Section
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Tagline (above headline)</label>
          <Input value={data.tagline || ''} onChange={(e) => setData({...data, tagline: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Highlight Text (colored text)</label>
          <Input value={data.highlight_text || ''} onChange={(e) => setData({...data, highlight_text: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Main Headline</label>
        <Input value={data.headline || ''} onChange={(e) => setData({...data, headline: e.target.value})} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Description</label>
        <Textarea value={data.description || ''} onChange={(e) => setData({...data, description: e.target.value})} rows={3} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Primary Button Text</label>
          <Input value={data.cta_button_text || ''} onChange={(e) => setData({...data, cta_button_text: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Secondary Button Text</label>
          <Input value={data.secondary_button_text || ''} onChange={(e) => setData({...data, secondary_button_text: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Trust Badge 1</label>
          <Input value={data.trust_badge_1 || ''} onChange={(e) => setData({...data, trust_badge_1: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Trust Badge 2</label>
          <Input value={data.trust_badge_2 || ''} onChange={(e) => setData({...data, trust_badge_2: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Trust Badge 3</label>
          <Input value={data.trust_badge_3 || ''} onChange={(e) => setData({...data, trust_badge_3: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Hero Section'}
      </Button>
    </div>
  );
};

// About Editor Component
const AboutEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || {});

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white flex items-center gap-2">
        <Info className="w-5 h-5 text-[#D4AF37]" /> About Us Section
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Section Title</label>
          <Input value={data.title || ''} onChange={(e) => setData({...data, title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Tagline</label>
          <Input value={data.tagline || ''} onChange={(e) => setData({...data, tagline: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Description Paragraph 1 (Always visible)</label>
        <Textarea value={data.description_1 || ''} onChange={(e) => setData({...data, description_1: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Description Paragraph 2 (Shows on "Know More")</label>
        <Textarea value={data.description_2 || ''} onChange={(e) => setData({...data, description_2: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Description Paragraph 3 (Shows on "Know More")</label>
        <Textarea value={data.description_3 || ''} onChange={(e) => setData({...data, description_3: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>

      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Extra Content (Shows on "Know More" - Optional)</label>
        <Textarea value={data.about_extra || ''} onChange={(e) => setData({...data, about_extra: e.target.value})} rows={2} placeholder="Add more details that appear when user clicks Know More" className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <hr className="border-white/10" />
      
      <h3 className="text-lg font-medium text-white">Mission Card</h3>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Mission Title</label>
        <Input value={data.mission_title || ''} onChange={(e) => setData({...data, mission_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Mission Text (Always visible)</label>
        <Textarea value={data.mission_text || ''} onChange={(e) => setData({...data, mission_text: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Mission Text 2 (Shows on "Know More")</label>
        <Textarea value={data.mission_text_2 || ''} onChange={(e) => setData({...data, mission_text_2: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Mission Extra (Shows on "Know More" - Optional)</label>
        <Textarea value={data.mission_extra || ''} onChange={(e) => setData({...data, mission_extra: e.target.value})} rows={2} placeholder="Add more mission details" className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <hr className="border-white/10" />
      
      <h3 className="text-lg font-medium text-white">Vision Card</h3>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Vision Title</label>
        <Input value={data.vision_title || ''} onChange={(e) => setData({...data, vision_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Vision Text (Always visible)</label>
        <Textarea value={data.vision_text || ''} onChange={(e) => setData({...data, vision_text: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Vision Tags (Shows on "Know More" - separate with |)</label>
        <Input value={data.vision_tags || ''} onChange={(e) => setData({...data, vision_tags: e.target.value})} placeholder="Tag 1|Tag 2|Tag 3" className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Vision Text 2 (Shows on "Know More")</label>
        <Textarea value={data.vision_text_2 || ''} onChange={(e) => setData({...data, vision_text_2: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
      </div>
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Vision Extra (Shows on "Know More" - Optional)</label>
        <Textarea value={data.vision_extra || ''} onChange={(e) => setData({...data, vision_extra: e.target.value})} rows={2} placeholder="Add more vision details" className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save About Section'}
      </Button>
    </div>
  );
};

// How It Works Editor
const HowItWorksEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || { items: [] });

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  const updateItem = (index, field, value) => {
    const newItems = [...(data.items || [])];
    newItems[index] = { ...newItems[index], [field]: value };
    setData({ ...data, items: newItems });
  };

  const addItem = () => {
    setData({ ...data, items: [...(data.items || []), { title: '', description: '', icon: 'Lightbulb' }] });
  };

  const removeItem = (index) => {
    const newItems = (data.items || []).filter((_, i) => i !== index);
    setData({ ...data, items: newItems });
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-[#D4AF37]" /> How It Works Section
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Section Title</label>
          <Input value={data.section_title || ''} onChange={(e) => setData({...data, section_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Section Subtitle</label>
          <Input value={data.section_subtitle || ''} onChange={(e) => setData({...data, section_subtitle: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-white">Items</h3>
          <Button onClick={addItem} variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37]">
            <Plus className="w-4 h-4 mr-1" /> Add Item
          </Button>
        </div>
        
        {(data.items || []).map((item, index) => (
          <div key={index} className="bg-[#141414] border border-white/10 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[#D4AF37] font-medium">Item {index + 1}</span>
              <Button onClick={() => removeItem(index)} variant="ghost" size="sm" className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <Input value={item.title || ''} onChange={(e) => updateItem(index, 'title', e.target.value)} placeholder="Title" className="bg-black/50 border-white/10 text-white" />
            <Textarea value={item.description || ''} onChange={(e) => updateItem(index, 'description', e.target.value)} placeholder="Description" rows={2} className="bg-black/50 border-white/10 text-white" />
          </div>
        ))}
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save How It Works'}
      </Button>
    </div>
  );
};

// Plans Editor
const PlansEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || { plans: [] });

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  const updatePlan = (index, field, value) => {
    const newPlans = [...(data.plans || [])];
    newPlans[index] = { ...newPlans[index], [field]: value };
    setData({ ...data, plans: newPlans });
  };

  const addPlan = () => {
    setData({ ...data, plans: [...(data.plans || []), { name: '', return_rate: '', return_type: '', description: '', min_amount: '', bg_color: 'gold' }] });
  };

  const removePlan = (index) => {
    const newPlans = (data.plans || []).filter((_, i) => i !== index);
    setData({ ...data, plans: newPlans });
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-[#D4AF37]" /> Payment Plans Section
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Section Title</label>
          <Input value={data.section_title || ''} onChange={(e) => setData({...data, section_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Subtitle</label>
          <Input value={data.section_subtitle || ''} onChange={(e) => setData({...data, section_subtitle: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Description</label>
          <Input value={data.section_description || ''} onChange={(e) => setData({...data, section_description: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-white">Plans</h3>
          <Button onClick={addPlan} variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37]">
            <Plus className="w-4 h-4 mr-1" /> Add Plan
          </Button>
        </div>
        
        {(data.plans || []).map((plan, index) => (
          <div key={index} className="bg-[#141414] border border-white/10 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[#D4AF37] font-medium">Plan {index + 1}</span>
              <Button onClick={() => removePlan(index)} variant="ghost" size="sm" className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Input value={plan.name || ''} onChange={(e) => updatePlan(index, 'name', e.target.value)} placeholder="Plan Name" className="bg-black/50 border-white/10 text-white" />
              <Input value={plan.return_rate || ''} onChange={(e) => updatePlan(index, 'return_rate', e.target.value)} placeholder="Return Rate (e.g., 30%)" className="bg-black/50 border-white/10 text-white" />
              <Input value={plan.return_type || ''} onChange={(e) => updatePlan(index, 'return_type', e.target.value)} placeholder="Return Type" className="bg-black/50 border-white/10 text-white" />
              <Input value={plan.min_amount || ''} onChange={(e) => updatePlan(index, 'min_amount', e.target.value)} placeholder="Min Amount" className="bg-black/50 border-white/10 text-white" />
            </div>
            <Textarea value={plan.description || ''} onChange={(e) => updatePlan(index, 'description', e.target.value)} placeholder="Description" rows={2} className="bg-black/50 border-white/10 text-white" />
          </div>
        ))}
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Plans'}
      </Button>
    </div>
  );
};

// Why Choose Us Editor
const WhyChooseEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || { features: [] });

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  const updateFeature = (index, field, value) => {
    const newFeatures = [...(data.features || [])];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setData({ ...data, features: newFeatures });
  };

  const addFeature = () => {
    setData({ ...data, features: [...(data.features || []), { title: '', description: '', icon: 'Shield' }] });
  };

  const removeFeature = (index) => {
    const newFeatures = (data.features || []).filter((_, i) => i !== index);
    setData({ ...data, features: newFeatures });
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white">Why Choose Us Section</h2>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Section Title</label>
        <Input value={data.section_title || ''} onChange={(e) => setData({...data, section_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-white">Features</h3>
          <Button onClick={addFeature} variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37]">
            <Plus className="w-4 h-4 mr-1" /> Add Feature
          </Button>
        </div>
        
        {(data.features || []).map((feature, index) => (
          <div key={index} className="bg-[#141414] border border-white/10 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[#D4AF37] font-medium">Feature {index + 1}</span>
              <Button onClick={() => removeFeature(index)} variant="ghost" size="sm" className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input value={feature.title || ''} onChange={(e) => updateFeature(index, 'title', e.target.value)} placeholder="Title" className="bg-black/50 border-white/10 text-white" />
              <Input value={feature.description || ''} onChange={(e) => updateFeature(index, 'description', e.target.value)} placeholder="Description" className="bg-black/50 border-white/10 text-white" />
            </div>
          </div>
        ))}
      </div>
      
      <hr className="border-white/10" />
      
      <h3 className="text-lg font-medium text-white">CTA Box</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input value={data.cta_tagline || ''} onChange={(e) => setData({...data, cta_tagline: e.target.value})} placeholder="CTA Tagline" className="bg-black/50 border-white/10 text-white" />
        <Input value={data.cta_title || ''} onChange={(e) => setData({...data, cta_title: e.target.value})} placeholder="CTA Title" className="bg-black/50 border-white/10 text-white" />
        <Input value={data.cta_description || ''} onChange={(e) => setData({...data, cta_description: e.target.value})} placeholder="CTA Description" className="bg-black/50 border-white/10 text-white" />
        <Input value={data.cta_button_text || ''} onChange={(e) => setData({...data, cta_button_text: e.target.value})} placeholder="CTA Button Text" className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Why Choose Us'}
      </Button>
    </div>
  );
};

// How to Start Editor
const HowToStartEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || { steps: [] });

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  const updateStep = (index, field, value) => {
    const newSteps = [...(data.steps || [])];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setData({ ...data, steps: newSteps });
  };

  const addStep = () => {
    const newNumber = String((data.steps || []).length + 1).padStart(2, '0');
    setData({ ...data, steps: [...(data.steps || []), { number: newNumber, title: '', description: '', icon: 'Phone' }] });
  };

  const removeStep = (index) => {
    const newSteps = (data.steps || []).filter((_, i) => i !== index);
    setData({ ...data, steps: newSteps });
  };

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white flex items-center gap-2">
        <PlayCircle className="w-5 h-5 text-[#D4AF37]" /> How to Start Section
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Section Title</label>
          <Input value={data.section_title || ''} onChange={(e) => setData({...data, section_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Section Subtitle</label>
          <Input value={data.section_subtitle || ''} onChange={(e) => setData({...data, section_subtitle: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-white">Steps</h3>
          <Button onClick={addStep} variant="outline" size="sm" className="border-[#D4AF37] text-[#D4AF37]">
            <Plus className="w-4 h-4 mr-1" /> Add Step
          </Button>
        </div>
        
        {(data.steps || []).map((step, index) => (
          <div key={index} className="bg-[#141414] border border-white/10 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-[#D4AF37] font-medium">Step {step.number || index + 1}</span>
              <Button onClick={() => removeStep(index)} variant="ghost" size="sm" className="text-red-500">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input value={step.number || ''} onChange={(e) => updateStep(index, 'number', e.target.value)} placeholder="Number (01, 02...)" className="bg-black/50 border-white/10 text-white" />
              <Input value={step.title || ''} onChange={(e) => updateStep(index, 'title', e.target.value)} placeholder="Title" className="bg-black/50 border-white/10 text-white" />
            </div>
            <Textarea value={step.description || ''} onChange={(e) => updateStep(index, 'description', e.target.value)} placeholder="Description" rows={2} className="bg-black/50 border-white/10 text-white" />
          </div>
        ))}
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save How to Start'}
      </Button>
    </div>
  );
};

// Footer Editor
const FooterEditor = ({ content, onSave, saving }) => {
  const [data, setData] = useState(content || {});

  useEffect(() => {
    if (content) setData(content);
  }, [content]);

  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-6 space-y-6">
      <h2 className="text-xl font-medium text-white flex items-center gap-2">
        <MessageSquare className="w-5 h-5 text-[#D4AF37]" /> Footer Section
      </h2>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Company Description</label>
        <Textarea value={data.description || ''} onChange={(e) => setData({...data, description: e.target.value})} rows={3} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Schedule Section Title</label>
          <Input value={data.schedule_title || ''} onChange={(e) => setData({...data, schedule_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Contact Title</label>
          <Input value={data.contact_title || ''} onChange={(e) => setData({...data, contact_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Instagram Handle</label>
          <Input value={data.instagram_handle || ''} onChange={(e) => setData({...data, instagram_handle: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Quick Links Title</label>
          <Input value={data.quick_links_title || ''} onChange={(e) => setData({...data, quick_links_title: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
      </div>
      
      <div>
        <label className="block text-[#A3A3A3] text-sm mb-2">Copyright Text</label>
        <Input value={data.copyright_text || ''} onChange={(e) => setData({...data, copyright_text: e.target.value})} className="bg-black/50 border-white/10 text-white" />
      </div>
      
      <Button onClick={() => onSave(data)} disabled={saving} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
        <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Footer'}
      </Button>
    </div>
  );
};

// Urgency Meter Management - SIMPLE: Percentage Only
const UrgencyMeterPage = () => {
  const [percentage, setPercentage] = useState(80);
  const [monthLabel, setMonthLabel] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/urgency-meter`);
        if (res.data) {
          setPercentage(res.data.percentage || 80);
          setMonthLabel(res.data.month_label || '');
        }
      } catch (error) {
        console.error('Error fetching urgency meter:', error);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/urgency-meter`, {
        percentage: percentage,
        month_label: monthLabel
      });
      toast.success('Urgency meter updated!');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="urgency-meter-page">
      <h1 className="text-3xl font-heading font-medium text-white mb-8">Urgency Meter</h1>
      
      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8 space-y-8">
          
          {/* Percentage Slider */}
          <div>
            <label className="block text-white font-medium mb-4">
              Funding Progress: <span className="text-[#D4AF37] text-2xl">{percentage}%</span>
            </label>
            <input 
              type="range" 
              min="0" 
              max="100" 
              value={percentage} 
              onChange={(e) => setPercentage(Number(e.target.value))}
              className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#D4AF37]"
            />
            <div className="flex justify-between text-[#A3A3A3] text-xs mt-2">
              <span>0%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>
          
          {/* Month Label */}
          <div>
            <label className="block text-white font-medium mb-2">Month Label</label>
            <Input 
              type="text" 
              value={monthLabel} 
              onChange={(e) => setMonthLabel(e.target.value)} 
              placeholder="e.g., March 2026" 
              className="bg-black/50 border-white/10 text-white"
            />
          </div>
          
          {/* Preview */}
          <div className="bg-[#141414] border border-[#D4AF37]/30 rounded-xl p-6">
            <h3 className="text-[#D4AF37] font-medium mb-4 text-center">Preview</h3>
            <div className="flex items-center justify-center">
              <div className="text-center">
                <div className="relative w-32 h-32">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle cx="64" cy="64" r="56" stroke="#333" strokeWidth="10" fill="none" />
                    <circle cx="64" cy="64" r="56" stroke="#D4AF37" strokeWidth="10" fill="none" 
                      strokeDasharray={`${percentage * 3.52} 352`} strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[#A3A3A3] text-xs">{monthLabel || 'Month'}</span>
                    <span className="text-white text-2xl font-bold">{percentage}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <Button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black hover:bg-[#F5C518] py-3">
            <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : 'Save'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Calculator Settings Page
const CalculatorPage = () => {
  const [settings, setSettings] = useState({
    monthly_rate: 5,
    lump_sum_rate: 30,
    lump_sum_months: 6,
    min_investment: 100000,
    max_investment: 10000000
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/calculator-settings`);
        if (res.data) {
          setSettings({
            monthly_rate: res.data.monthly_rate || 5,
            lump_sum_rate: res.data.lump_sum_rate || res.data.six_month_rate || 30,
            lump_sum_months: res.data.lump_sum_months || 6,
            min_investment: res.data.min_investment || 100000,
            max_investment: res.data.max_investment || 10000000
          });
        }
      } catch (error) {
        console.error('Error fetching calculator settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/calculator-settings`, settings);
      toast.success('Calculator settings updated!');
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(value);
  };

  // Preview calculation
  const previewAmount = 500000;
  const monthlyReturn = previewAmount * (settings.monthly_rate / 100);
  const lumpSumReturn = previewAmount * (settings.lump_sum_rate / 100);

  return (
    <div data-testid="calculator-page">
      <h1 className="text-3xl font-heading font-medium text-white mb-8">Calculator Settings</h1>
      
      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/10 rounded-lg p-8 space-y-8">
          
          {/* Monthly Return Rate */}
          <div>
            <label className="block text-white font-medium mb-4">
              Monthly Return Rate: <span className="text-[#D4AF37] text-2xl">{settings.monthly_rate}%</span>
            </label>
            <input 
              type="range" 
              min="1" 
              max="10" 
              step="0.5"
              value={settings.monthly_rate} 
              onChange={(e) => setSettings({ ...settings, monthly_rate: Number(e.target.value) })}
              className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#D4AF37]"
            />
            <div className="flex justify-between text-[#A3A3A3] text-xs mt-2">
              <span>1%</span>
              <span>5%</span>
              <span>10%</span>
            </div>
          </div>

          {/* Time Period Selection */}
          <div>
            <label className="block text-white font-medium mb-3">Lump Sum Return Period</label>
            <div className="grid grid-cols-3 gap-3">
              {[6, 12, 24].map((months) => (
                <button
                  key={months}
                  type="button"
                  onClick={() => setSettings({ ...settings, lump_sum_months: months })}
                  className={`py-3 px-4 rounded-lg font-medium transition-all ${
                    settings.lump_sum_months === months 
                      ? 'bg-[#D4AF37] text-black' 
                      : 'bg-black/50 text-white border border-white/10 hover:border-[#D4AF37]/50'
                  }`}
                >
                  {months} Months
                </button>
              ))}
            </div>
          </div>

          {/* Lump Sum Return Rate */}
          <div>
            <label className="block text-white font-medium mb-4">
              {settings.lump_sum_months}-Month Return Rate: <span className="text-[#D4AF37] text-2xl">{settings.lump_sum_rate}%</span>
            </label>
            <input 
              type="range" 
              min="5" 
              max="100" 
              step="1"
              value={settings.lump_sum_rate} 
              onChange={(e) => setSettings({ ...settings, lump_sum_rate: Number(e.target.value) })}
              className="w-full h-3 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#D4AF37]"
            />
            <div className="flex justify-between text-[#A3A3A3] text-xs mt-2">
              <span>5%</span>
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Min Investment */}
          <div>
            <label className="block text-white font-medium mb-2">Minimum Investment</label>
            <Input 
              type="number" 
              min="10000"
              step="10000"
              value={settings.min_investment} 
              onChange={(e) => setSettings({ ...settings, min_investment: Number(e.target.value) })} 
              className="bg-black/50 border-white/10 text-white"
            />
            <p className="text-[#A3A3A3] text-xs mt-1">Current: {formatCurrency(settings.min_investment)}</p>
          </div>

          {/* Max Investment */}
          <div>
            <label className="block text-white font-medium mb-2">Maximum Investment</label>
            <Input 
              type="number" 
              min="100000"
              step="100000"
              value={settings.max_investment} 
              onChange={(e) => setSettings({ ...settings, max_investment: Number(e.target.value) })} 
              className="bg-black/50 border-white/10 text-white"
            />
            <p className="text-[#A3A3A3] text-xs mt-1">Current: {formatCurrency(settings.max_investment)}</p>
          </div>
          
          {/* Preview */}
          <div className="bg-[#141414] border border-[#D4AF37]/30 rounded-xl p-6">
            <h3 className="text-[#D4AF37] font-medium mb-4 text-center">Preview (₹5 Lakh Investment)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-black/30 rounded-lg">
                <p className="text-[#A3A3A3] text-sm">Monthly Return ({settings.monthly_rate}%)</p>
                <p className="text-white text-xl font-bold">{formatCurrency(monthlyReturn)}</p>
              </div>
              <div className="text-center p-4 bg-[#D4AF37]/20 rounded-lg">
                <p className="text-[#A3A3A3] text-sm">{settings.lump_sum_months}-Month Return ({settings.lump_sum_rate}%)</p>
                <p className="text-[#D4AF37] text-xl font-bold">{formatCurrency(lumpSumReturn)}</p>
              </div>
            </div>
          </div>
          
          <Button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black hover:bg-[#F5C518] py-3">
            <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : 'Save Calculator Settings'}
          </Button>
        </form>
      </div>
    </div>
  );
};

// Testimonials, Social Links, Leads, Settings pages remain the same...
const TestimonialsPage = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ 
    investor_name: '', 
    designation: '',
    location: '',
    testimonial_text: '', 
    rating: 5,
    image_url: '', 
    investment_amount: '',
    order: 0, 
    is_active: true 
  });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => { fetchTestimonials(); }, []);

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/admin/testimonials`);
      setTestimonials(res.data || []);
    } catch (error) { console.error('Error:', error); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, WebP, or GIF');
      return;
    }
    
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size: 10MB');
      return;
    }
    
    setIsUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/upload/thumbnail`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const baseUrl = API.replace('/api', '');
      const fullUrl = `${baseUrl}${res.data.url}`;
      setFormData({...formData, image_url: fullUrl});
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/testimonials/${editingItem.id}`, formData);
      } else {
        await axios.post(`${API}/testimonials`, formData);
      }
      toast.success(editingItem ? 'Updated!' : 'Added!');
      setIsDialogOpen(false);
      setEditingItem(null);
      setFormData({ investor_name: '', designation: '', location: '', testimonial_text: '', rating: 5, image_url: '', investment_amount: '', order: 0, is_active: true });
      fetchTestimonials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this testimonial?')) {
      await axios.delete(`${API}/testimonials/${id}`);
      toast.success('Deleted!');
      fetchTestimonials();
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ investor_name: '', designation: '', location: '', testimonial_text: '', rating: 5, image_url: '', investment_amount: '', order: 0, is_active: true });
  };

  return (
    <div data-testid="testimonials-page">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-heading font-medium text-white">Investor Testimonials</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#F5C518]" onClick={resetForm}>
              <Plus className="w-4 h-4 mr-2" /> Add Testimonial
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">{editingItem ? 'Edit' : 'Add'} Testimonial</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#A3A3A3] mb-1 block">Investor Name *</label>
                  <Input value={formData.investor_name} onChange={(e) => setFormData({...formData, investor_name: e.target.value})} required placeholder="e.g., Rajesh Sharma" className="bg-black/50 border-white/10 text-white" />
                </div>
                <div>
                  <label className="text-sm text-[#A3A3A3] mb-1 block">Designation</label>
                  <Input value={formData.designation} onChange={(e) => setFormData({...formData, designation: e.target.value})} placeholder="e.g., Business Owner" className="bg-black/50 border-white/10 text-white" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#A3A3A3] mb-1 block">Location</label>
                  <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} placeholder="e.g., Mumbai" className="bg-black/50 border-white/10 text-white" />
                </div>
                <div>
                  <label className="text-sm text-[#A3A3A3] mb-1 block">Investment Amount</label>
                  <Input value={formData.investment_amount} onChange={(e) => setFormData({...formData, investment_amount: e.target.value})} placeholder="e.g., ₹5,00,000" className="bg-black/50 border-white/10 text-white" />
                </div>
              </div>
              <div>
                <label className="text-sm text-[#A3A3A3] mb-1 block">Testimonial Text *</label>
                <Textarea value={formData.testimonial_text} onChange={(e) => setFormData({...formData, testimonial_text: e.target.value})} required placeholder="What did the investor say about their experience?" rows={4} className="bg-black/50 border-white/10 text-white" />
              </div>
              <div>
                <label className="text-sm text-[#A3A3A3] mb-1 block">Rating (1-5 Stars)</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({...formData, rating: star})}
                      className={`p-1 rounded ${formData.rating >= star ? 'text-[#D4AF37]' : 'text-gray-500'}`}
                    >
                      <svg className="w-6 h-6" fill={formData.rating >= star ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm text-[#A3A3A3] mb-1 block">Investor Photo</label>
                <div className="flex gap-2">
                  <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="Paste URL or upload" className="bg-black/50 border-white/10 text-white flex-1" />
                  <input 
                    type="file" 
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="bg-white/10 hover:bg-white/20 text-white"
                  >
                    {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  </Button>
                </div>
                {formData.image_url && (
                  <div className="mt-2 w-16 h-16 rounded-full overflow-hidden border-2 border-[#D4AF37]">
                    <img src={formData.image_url} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <p className="text-xs text-[#666] mt-1">Use a square face photo for best results</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-[#A3A3A3] mb-1 block">Display Order</label>
                  <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: Number(e.target.value)})} placeholder="0" className="bg-black/50 border-white/10 text-white" />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch checked={formData.is_active} onCheckedChange={(c) => setFormData({...formData, is_active: c})} />
                  <span className="text-[#A3A3A3]">Active</span>
                </div>
              </div>
              <DialogFooter><Button type="submit" className="bg-[#D4AF37] text-black">Save Testimonial</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-[#A3A3A3] w-16">Photo</TableHead>
              <TableHead className="text-[#A3A3A3]">Name</TableHead>
              <TableHead className="text-[#A3A3A3]">Testimonial</TableHead>
              <TableHead className="text-[#A3A3A3]">Status</TableHead>
              <TableHead className="text-[#A3A3A3]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {testimonials.map((t) => (
              <TableRow key={t.id} className="border-white/10">
                <TableCell>
                  <div className="w-12 h-12 rounded-full overflow-hidden bg-black/30 border border-[#D4AF37]">
                    {t.image_url ? (
                      <img src={t.image_url} alt={t.investor_name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#666] text-xs">No img</div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="text-white font-medium">{t.investor_name}</div>
                  <div className="text-[#A3A3A3] text-xs">{t.designation}{t.location && `, ${t.location}`}</div>
                </TableCell>
                <TableCell>
                  <div className="text-[#A3A3A3] text-sm max-w-xs truncate">{t.testimonial_text || 'No text'}</div>
                </TableCell>
                <TableCell>
                  <span className={`px-2 py-1 rounded text-xs ${t.is_active ? 'bg-[#22C55E]/20 text-[#22C55E]' : 'bg-red-500/20 text-red-500'}`}>
                    {t.is_active ? 'Active' : 'Inactive'}
                  </span>
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => { setEditingItem(t); setFormData(t); setIsDialogOpen(true); }} className="text-[#A3A3A3]"><Edit className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {testimonials.length === 0 && (
          <div className="text-center py-8 text-[#A3A3A3]">No testimonials yet. Click "Add Testimonial" to create one.</div>
        )}
      </div>
    </div>
  );
};

const SocialLinksPage = () => {
  const [links, setLinks] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ platform: '', url: '', is_active: true });

  useEffect(() => { fetchLinks(); }, []);
  const fetchLinks = async () => { const res = await axios.get(`${API}/admin/social-links`); setLinks(res.data || []); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/social-links`, formData);
    toast.success('Added!');
    setIsDialogOpen(false);
    fetchLinks();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/social-links/${id}`);
    toast.success('Deleted!');
    fetchLinks();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-heading font-medium text-white">Social Links</h1>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild><Button className="bg-[#D4AF37] text-black"><Plus className="w-4 h-4 mr-2" /> Add</Button></DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-white/10">
            <DialogHeader><DialogTitle className="text-white">Add Social Link</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <select value={formData.platform} onChange={(e) => setFormData({...formData, platform: e.target.value})} required className="w-full bg-black/50 border border-white/10 text-white rounded-md h-10 px-3">
                <option value="">Select platform</option>
                {['whatsapp','instagram','facebook','linkedin','youtube','twitter'].map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <Input value={formData.url} onChange={(e) => setFormData({...formData, url: e.target.value})} required placeholder="URL" className="bg-black/50 border-white/10 text-white" />
              <DialogFooter><Button type="submit" className="bg-[#D4AF37] text-black">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow className="border-white/10"><TableHead className="text-[#A3A3A3]">Platform</TableHead><TableHead className="text-[#A3A3A3]">URL</TableHead><TableHead className="text-[#A3A3A3]">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {links.map((l) => (
              <TableRow key={l.id} className="border-white/10">
                <TableCell className="text-white capitalize">{l.platform}</TableCell>
                <TableCell className="text-[#A3A3A3] truncate max-w-xs">{l.url}</TableCell>
                <TableCell><Button variant="ghost" size="sm" onClick={() => handleDelete(l.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  useEffect(() => { axios.get(`${API}/admin/leads`).then(res => setLeads(res.data || [])); }, []);

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/leads/${id}/status?status=${status}`);
    toast.success('Updated!');
    axios.get(`${API}/admin/leads`).then(res => setLeads(res.data || []));
  };

  return (
    <div>
      <h1 className="text-3xl font-heading font-medium text-white mb-8">Leads</h1>
      <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader><TableRow className="border-white/10"><TableHead className="text-[#A3A3A3]">Name</TableHead><TableHead className="text-[#A3A3A3]">Contact</TableHead><TableHead className="text-[#A3A3A3]">Amount</TableHead><TableHead className="text-[#A3A3A3]">Status</TableHead></TableRow></TableHeader>
          <TableBody>
            {leads.map((l) => (
              <TableRow key={l.id} className="border-white/10">
                <TableCell className="text-white">{l.name}</TableCell>
                <TableCell className="text-[#A3A3A3] text-sm">{l.email}<br/>{l.phone}</TableCell>
                <TableCell className="text-[#D4AF37]">{l.investment_amount ? `₹${l.investment_amount.toLocaleString()}` : '-'}</TableCell>
                <TableCell>
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="bg-black/50 border border-white/10 text-white text-sm rounded px-2 py-1">
                    <option value="new">New</option>
                    <option value="contacted">Contacted</option>
                    <option value="converted">Converted</option>
                  </select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const SettingsPage = () => {
  const [settings, setSettings] = useState({ whatsapp_number: '', whatsapp_message: '', contact_email: '', contact_phone: '', address: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { axios.get(`${API}/settings`).then(res => res.data && setSettings(res.data)); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await axios.post(`${API}/settings`, settings);
    toast.success('Saved!');
    setLoading(false);
  };

  return (
    <div>
      <h1 className="text-3xl font-heading font-medium text-white mb-8">Settings</h1>
      <form onSubmit={handleSubmit} className="max-w-2xl bg-[#0A0A0A] border border-white/10 rounded-lg p-8 space-y-6">
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">WhatsApp Number</label>
          <Input value={settings.whatsapp_number} onChange={(e) => setSettings({...settings, whatsapp_number: e.target.value})} placeholder="919876543210" className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">WhatsApp Message</label>
          <Textarea value={settings.whatsapp_message} onChange={(e) => setSettings({...settings, whatsapp_message: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Contact Email</label>
          <Input value={settings.contact_email} onChange={(e) => setSettings({...settings, contact_email: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Contact Phone</label>
          <Input value={settings.contact_phone} onChange={(e) => setSettings({...settings, contact_phone: e.target.value})} className="bg-black/50 border-white/10 text-white" />
        </div>
        <div>
          <label className="block text-[#A3A3A3] text-sm mb-2">Address</label>
          <Textarea value={settings.address || ''} onChange={(e) => setSettings({...settings, address: e.target.value})} rows={2} className="bg-black/50 border-white/10 text-white" />
        </div>
        <Button type="submit" disabled={loading} className="bg-[#D4AF37] text-black hover:bg-[#F5C518]">
          <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : 'Save Settings'}
        </Button>
      </form>
    </div>
  );
};

// Main Admin Dashboard
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="lg:ml-64">
        <header className="sticky top-0 z-30 bg-[#050505]/90 backdrop-blur-lg border-b border-white/5 px-6 py-4">
          <div className="flex items-center justify-between">
            <button className="lg:hidden text-white" onClick={() => setSidebarOpen(true)}><Menu size={24} /></button>
            <span className="text-[#A3A3A3] text-sm">Admin Panel</span>
          </div>
        </header>
        <main className="p-6 md:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="content" element={<ContentEditorPage />} />
            <Route path="urgency-meter" element={<UrgencyMeterPage />} />
            <Route path="calculator" element={<CalculatorPage />} />
            <Route path="testimonials" element={<TestimonialsPage />} />
            <Route path="social-links" element={<SocialLinksPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="settings" element={<SettingsPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
